import type { LucideIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { createContext, useContext, useId, type ReactNode } from 'react';

import { cn } from '../../lib/cn.js';
import { TRANSITION } from '../motion.js';

/**
 * Abas sublinhadas (design.md §8, Aba).
 *
 * Na horizontal as abas são texto sobre um filete `line` que corre por baixo
 * delas; a ativa ganha tinta, peso 500 e um sublinhado `primary` de 2px por
 * cima do filete. O sublinhado é um elemento só por lista e **desliza** até a
 * aba clicada em vez de sumir e reaparecer — é o `layoutId` do motion,
 * partilhado por todas as abas da mesma lista. Quadradas: sem trilho, sem
 * fundo e sem raio.
 *
 * O filete é sombra por dentro, e não borda: a lista rola na horizontal no
 * celular, e o que passasse da caixa — o sublinhado sobre a borda — seria
 * cortado pela rolagem.
 *
 * Na vertical — as etapas do formulário — não há filete: a etapa ativa é o
 * afundado com a barra laranja de 2px à esquerda, a do item ativo do trilho, e
 * também desliza.
 */
const TabListContext = createContext<{ id: string; vertical: boolean }>({
  id: 'abas',
  vertical: false,
});

export function TabList({
  label,
  orientation = 'horizontal',
  children,
}: {
  label: string;
  orientation?: 'horizontal' | 'vertical';
  children: ReactNode;
}) {
  const id = useId();
  const vertical = orientation === 'vertical';

  return (
    <TabListContext.Provider value={{ id, vertical }}>
      <div
        role="tablist"
        aria-label={label}
        aria-orientation={orientation}
        className={cn(
          vertical
            ? 'flex w-full flex-col gap-0.5'
            : 'flex h-10 max-w-full items-stretch overflow-x-auto shadow-[inset_0_-1px_0_var(--color-line)]',
        )}
      >
        {children}
      </div>
    </TabListContext.Provider>
  );
}

export function Tab({
  active,
  onClick,
  count,
  icon: Icon,
  disabled = false,
  title,
  vertical = false,
  /** Ponto de aviso à direita: a etapa tem campo com erro. */
  alert = false,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count?: number | undefined;
  icon?: LucideIcon | undefined;
  disabled?: boolean | undefined;
  title?: string | undefined;
  vertical?: boolean | undefined;
  alert?: boolean | undefined;
  children: ReactNode;
}) {
  const list = useContext(TabListContext);
  const isVertical = vertical || list.vertical;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={cn(
        'relative flex shrink-0 items-center gap-2 text-[13px] whitespace-nowrap transition-[color,box-shadow] duration-100',
        isVertical ? 'h-9 w-full px-3 text-left' : 'gap-1.5 px-3',
        active ? 'font-medium text-ink' : 'text-ink-3 hover:text-ink',
        // Sob o ponteiro, a aba que não está ativa mostra onde o sublinhado iria.
        !active && !isVertical && 'hover:shadow-[inset_0_-2px_0_var(--color-line-2)]',
        disabled && 'cursor-not-allowed opacity-50 hover:text-ink-3 hover:shadow-none',
      )}
    >
      {active ? (
        <motion.span
          layoutId={`${list.id}-ativa`}
          transition={TRANSITION.layout}
          className={cn(
            'absolute',
            isVertical
              ? 'inset-0 bg-sunken shadow-[inset_2px_0_0_var(--color-primary)]'
              : 'inset-x-0 bottom-0 h-0.5 bg-primary',
          )}
          aria-hidden
        />
      ) : null}

      {Icon ? (
        <Icon
          size={15}
          strokeWidth={1.5}
          className={cn('relative z-10 shrink-0', active && 'text-primary')}
          aria-hidden
        />
      ) : null}
      <span className={cn('relative z-10', isVertical && 'min-w-0 flex-1 truncate')}>
        {children}
      </span>
      {count === undefined ? null : (
        <span
          className={cn('tabular relative z-10 text-[12px]', active ? 'text-ink-3' : 'opacity-70')}
        >
          {count}
        </span>
      )}
      {alert ? (
        <span
          aria-label="Esta etapa tem campos a corrigir"
          className="relative z-10 size-1.5 shrink-0 rounded-full bg-danger"
        />
      ) : null}
    </button>
  );
}
